import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { LocationIdSchema, WebsitePerformanceReportIdSchema } from '@brightlocal/shared';
import type { LocationId } from '@brightlocal/shared';

import { PaginationSchema } from '../../../schemas';

/** Query params of the paginated Website Performance reports list. */
export interface WebsitePerformanceGetReportsParams {
  /** Filter the list to a single location's report. */
  locationId?: LocationId;
  /** 1-indexed page number; the API defaults to 1 when omitted. */
  page?: number;
  /** Items per page (1-100); the API defaults to 25 when omitted. */
  numPerPage?: number;
}

/**
 * Single entry of the reports list. `website_url` is the website analysed in the report's most
 * recent run, as recorded at the time — not guaranteed to be an absolute URI.
 */
export const WebsitePerformanceReportListItemSchema = z
  .object({
    report_id: WebsitePerformanceReportIdSchema,
    location_id: LocationIdSchema,
    website_url: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type WebsitePerformanceReportListItem = z.infer<typeof WebsitePerformanceReportListItemSchema>;

/** Paginated list of the account's Website Performance reports. Only reports with at least one run are listed. */
export const WebsitePerformanceReportsResponseSchema = z.object({
  data: z.array(WebsitePerformanceReportListItemSchema),
  pagination: PaginationSchema
});

export type WebsitePerformanceReportsResponse = z.infer<typeof WebsitePerformanceReportsResponseSchema>;

/** Overall health score of a run. Issue counts are `null` when not reported. */
const WebsitePerformanceHealthScoreSchema = z.object({
  score: z.number(),
  band: z.string(),
  critical_issues_count: z.number().nullable(),
  total_issues_count: z.number().nullable()
});

/** Written summary of a run; `health_score` is `null` when the analysis has no score. */
const WebsitePerformanceHealthSummarySchema = z.object({
  overview: z.string(),
  health_score: WebsitePerformanceHealthScoreSchema.nullable(),
  top_priorities: z.array(z.string()),
  quick_wins: z.array(z.string()),
  key_opportunities: z.array(z.string()),
  narrative: z.string().nullable()
});

/**
 * A problem found on the website. `issue_id` is unique within the run and referenced by
 * recommendations; text fields are an empty string when none was written.
 */
const WebsitePerformanceIssueSchema = z.object({
  issue_id: z.string(),
  title: z.string(),
  category: z.string(),
  severity: z.string(),
  description: z.string(),
  impact: z.string(),
  affected_page_count: z.number().nullable()
});

/** A recommended action, addressing the issues listed in `issue_ids`. */
const WebsitePerformanceRecommendationSchema = z.object({
  title: z.string(),
  description: z.string(),
  priority: z.string(),
  impact: z.string(),
  effort: z.string(),
  issue_ids: z.array(z.string()),
  steps: z.array(z.string()),
  expected_outcome: z.string()
});

/** The Website Health result of a run. Issues and recommendations keep the order the analysis reported. */
const WebsitePerformanceResultSchema = z.object({
  summary: WebsitePerformanceHealthSummarySchema,
  issues: z.array(WebsitePerformanceIssueSchema),
  recommendations: z.array(WebsitePerformanceRecommendationSchema)
});

/**
 * Visit figures for a date range. `engagement_rate` is a percentage from 0 to 100 (not GA4's 0–1), rounded to 2 decimal
 * places, and `average_engagement_time_seconds` is the average per visit. Both are `null` when there were no visits.
 */
const VisitorsTrafficMetricsSchema = z.object({
  visits: z.number(),
  engaged_visits: z.number(),
  engagement_rate: z.number().nullable(),
  average_engagement_time_seconds: z.number().nullable()
});

/**
 * A landing page: the first page viewed in a visit. `path` passes GA4's placeholders through: `(not set)` when GA4
 * recorded no landing page, and `(other)` when it grouped low-traffic pages together.
 */
const VisitorsTopPageSchema = z.object({
  path: z.string(),
  visits: z.number(),
  engagement_rate: z.number(),
  average_engagement_time_seconds: z.number()
});

/** Figures for one traffic segment (all traffic or organic search) over a period. */
const VisitorsTrafficSegmentSchema = z.object({
  totals: VisitorsTrafficMetricsSchema,
  daily: z.array(VisitorsTrafficMetricsSchema.extend({ date: z.string() })),
  top_pages: z.array(VisitorsTopPageSchema)
});

/** GA4 default channel groups, in snake_case. */
const VISITORS_CHANNELS = [
  'direct',
  'organic_search',
  'paid_search',
  'organic_social',
  'paid_social',
  'email',
  'affiliates',
  'referral',
  'display',
  'organic_video',
  'paid_video',
  'cross_network',
  'organic_shopping',
  'paid_shopping',
  'paid_other',
  'audio',
  'sms',
  'mobile_push_notifications',
  'unassigned',
  'other'
] as const;

/** Visit figures for a 28-day period. `channels` are GA4 default channel groups, all traffic only. */
const VisitorsPeriodSchema = z.object({
  starts_on: z.string(),
  ends_on: z.string(),
  all_traffic: VisitorsTrafficSegmentSchema,
  organic_search: VisitorsTrafficSegmentSchema,
  channels: z.array(z.object({ channel: z.enum(VISITORS_CHANNELS), visits: z.number() }))
});

/**
 * GA4 visitor data collected with a run. `status` says whether the data is present and, if not, why;
 * the periods and `monthly` are `null` unless it is `available`. `monthly` can be empty while it is `available`,
 * e.g. for a property with no complete month of visits yet. `monthly[].month` is the first day of the month.
 */
const WebsitePerformanceVisitorsSchema = z.object({
  status: z.enum(['available', 'not_connected', 'connection_broken', 'no_data', 'unavailable']),
  generated_at: z.string().nullable(),
  current_period: VisitorsPeriodSchema.nullable(),
  previous_period: VisitorsPeriodSchema.nullable(),
  monthly: z
    .array(
      z.object({
        month: z.string(),
        all_traffic: VisitorsTrafficMetricsSchema,
        organic_search: VisitorsTrafficMetricsSchema
      })
    )
    .nullable()
});

/**
 * Latest completed run of a report. `website_url` is the website that was analysed, `created_at`
 * is when it was analysed (RFC 3339, UTC), and `result` is `null` when no analysis was recorded.
 * `visitors` is `null` or missing for runs completed before visitor data was introduced.
 */
export const WebsitePerformanceRunSchema = z
  .object({
    run_id: z.string(),
    report_id: WebsitePerformanceReportIdSchema,
    location_id: LocationIdSchema,
    website_url: z.string(),
    status: z.string(),
    created_at: z.string(),
    completed_at: z.string().nullable(),
    result: WebsitePerformanceResultSchema.nullable(),
    // TODO: https://brightlocal.atlassian.net/browse/PI-109 — make it required (`.nullable()`) once the API returns it
    visitors: WebsitePerformanceVisitorsSchema.nullish()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type WebsitePerformanceRun = z.infer<typeof WebsitePerformanceRunSchema>;

/** The Website Health result of a run. */
export type WebsitePerformanceResult = NonNullable<WebsitePerformanceRun['result']>;

/** A problem found on the website. */
export type WebsitePerformanceIssue = WebsitePerformanceResult['issues'][number];

/** A recommended action. */
export type WebsitePerformanceRecommendation = WebsitePerformanceResult['recommendations'][number];
